import React, { useEffect, useMemo, useState } from 'react'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { horizontalListSortingStrategy, rectSwappingStrategy, SortableContext } from '@dnd-kit/sortable'
import { AttachmentData } from '../../../../../../../../../../../@types/types'
import { convertImageUrl } from '../../../../../../../../../../../services/conversion/conversionServices'
import { SortablePictureItem } from '../sortablePictureItem/SortablePictureItem'
import { calculateDragPreviewSize, getInitialDragPreviewSize } from './dragPreviewUtils'
import styles from './DraggablePictureList.module.css'

export type DraggablePictureListVariant = 'grid' | 'strip'

export interface DraggablePictureListProps {
  attachments: AttachmentData[]
  selectedIds: string[]
  variant: DraggablePictureListVariant
  leadingContent?: React.ReactNode
  onSelect: (attachmentId: string) => void
  onReorder: (activeId: string, overId: string) => void
}

export const DraggablePictureList: React.FC<DraggablePictureListProps> = ({
  attachments,
  selectedIds,
  variant,
  leadingContent,
  onSelect,
  onReorder,
}) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier>()
  const isStrip = variant === 'strip'
  const [dragPreviewSize, setDragPreviewSize] = useState<number>(() => (isStrip ? 80 : getInitialDragPreviewSize()))

  useEffect(() => {
    if (isStrip) {
      setDragPreviewSize(85)
      return
    }

    const handleResize = () => setDragPreviewSize(calculateDragPreviewSize(window.innerWidth))
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isStrip])

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id))
    }
    setActiveId(undefined)
  }

  const activeAttachment = useMemo(
    () =>
      activeId === undefined ? null : attachments.find((item) => (item.attachment?.id || item.file?.id) === activeId),
    [activeId, attachments]
  )
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 5 } }),
    useSensor(KeyboardSensor)
  )
  const items = attachments.map((attachment) => attachment.attachment?.id || attachment.file?.id || '')

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(event) => setActiveId(event.active.id)}
      onDragEnd={handleDragEnd}
      collisionDetection={closestCenter}
      data-testid='draggable-picture-list'
    >
      <div className={`${styles[variant]} gap-m margin-bottom-l`}>
        {leadingContent}
        <SortableContext items={items} strategy={isStrip ? horizontalListSortingStrategy : rectSwappingStrategy}>
          {attachments.map((attachment, index) => {
            const attachmentId = attachment.attachment?.id || attachment.file?.id || ''

            return (
              <SortablePictureItem
                key={attachmentId || index}
                data={attachment}
                index={index}
                isSelected={selectedIds.includes(attachmentId)}
                onClick={() => onSelect(attachmentId)}
              />
            )
          })}
        </SortableContext>
      </div>
      <DragOverlay>
        {activeAttachment && (
          <div className={styles.dragOverlay} style={{ width: `${dragPreviewSize}px`, height: `${dragPreviewSize}px` }}>
            <div
              className={styles.dragOverlayImage}
              style={{
                backgroundImage: `url(${convertImageUrl('400', '400', activeAttachment.attachment?.url || '')})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}
